import React from 'react'

import { useAppDispatch, useAppSelector } from '@/hooks/reduxHooks'
import { getDashboardViewsData, updateDashboardViewsData } from '@/store/slices/dashboard-views-slice'

import { toDashboardViewsDto, toDashboardViewsState } from '../adapters/dashboard-views.adapter'
import type { DashboardViewsState } from '../model/dashboardViews'

export const useDashboardViews = (profileId?: string) => {
  const dispatch = useAppDispatch()
  const data = useAppSelector((store) => store.dashboardViews.data)
  const loadedProfileId = useAppSelector((store) => store.dashboardViews.profileId)
  const loadStatus = useAppSelector((store) => store.dashboardViews.loadStatus)
  const saveStatus = useAppSelector((store) => store.dashboardViews.saveStatus)
  const state = React.useMemo(() => toDashboardViewsState(data), [data])

  React.useEffect(() => {
    if (profileId && (loadedProfileId !== profileId || loadStatus === 'idle')) {
      void dispatch(getDashboardViewsData(profileId))
    }
  }, [dispatch, loadStatus, loadedProfileId, profileId])

  const commit = React.useCallback(
    async (next: DashboardViewsState) => {
      if (!profileId) throw new Error('DASHBOARD_VIEWS_NO_PROFILE')

      const response = await dispatch(
        updateDashboardViewsData({ profileId, state: toDashboardViewsDto(next) }),
      ).unwrap()
      return toDashboardViewsState(response)
    },
    [dispatch, profileId],
  )

  const reload = React.useCallback(() => {
    if (profileId) void dispatch(getDashboardViewsData(profileId))
  }, [dispatch, profileId])

  return { state, loadStatus, saveStatus, commit, reload }
}
